import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';
import dateService from '@gem/services/date-service';

import Tasks from 'APP/Tasks';

import { MeetingLinkPopup } from '../MeetingLinkPopup/MeetingLinkPopup';
import { INewMeetingFormData } from './NewMeetingPopup.types';

interface INewMeetingPopupPresenter {
  initialValues: INewMeetingFormData;
  isDirty: boolean;
  onSubmit: (data: INewMeetingFormData) => Promise<void>;
  setDirty: (value: boolean) => void;
  onClose: () => void;
  tryToClose: () => Promise<void>;
}

interface INewMeetingPopupPresenterReturnType {
  presenter: INewMeetingPopupPresenter;
  form: UseFormReturn<INewMeetingFormData>;
}

export function useNewMeetingPopupPresenter(
  data: ICommonPopupsProps
): INewMeetingPopupPresenterReturnType {
  const { popupInstance } = data;
  const { t } = useTranslation();

  const presenter = useLocalObservable<INewMeetingPopupPresenter>(() => ({
    initialValues: {
      roomName: t('new_meeting_modal_name_value', {
        0:
          dateService.format(new Date(), {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
          }) || '',
      }),
      allowGuests: false,
      waitingRoomEnabled: false,
      webinar: false,
    },
    isDirty: false,

    async onSubmit(params: INewMeetingFormData): Promise<void> {
      const response = await Tasks.calls.createRoom({
        roomName: params.roomName.trim(),
        allowGuests: params.allowGuests,
        waitingRoomEnabled: params.waitingRoomEnabled,
        webinar: params.webinar,
        // TODO: webinar remove after backend support
        initialParticipants: [],
      });

      if (response.result) {
        popupInstance.close();

        Tasks.app.openPopup(MeetingLinkPopup, {
          params: { link: response.link || '', channelId: response.channelId || '' },
        });
      } else {
        Tasks.app.showToast(t('something_went_wrong'));
      }
    },

    onClose(): void {
      presenter.tryToClose();
    },

    setDirty(value: boolean): void {
      presenter.isDirty = value;
    },

    async tryToClose(): Promise<void> {
      let canClose = true;

      if (presenter.isDirty) {
        canClose = await Tasks.app.showConfirm({ text: t('cancel_channel_creation_alert') });
      }

      if (canClose) {
        popupInstance.close();
      }
    },
  }));

  const form = useForm<INewMeetingFormData>({
    mode: 'onChange',
    defaultValues: presenter.initialValues,
  });

  useEffect(() => {
    presenter.setDirty(form.formState.isDirty);
  }, [form.formState.isDirty]);

  return { presenter, form };
}
